import { useState, useRef, useEffect } from "react";

const ICON_OPTIONS = [
  // Commerce & Store
  "ri-store-2-line", "ri-shopping-bag-line", "ri-shopping-cart-line", "ri-price-tag-3-line",
  "ri-bank-card-line", "ri-money-dollar-circle-line", "ri-exchange-dollar-line", "ri-wallet-line",
  // People & Community
  "ri-user-smile-line", "ri-team-line", "ri-group-line", "ri-heart-line", "ri-hand-heart-line",
  "ri-user-star-line", "ri-community-line", "ri-parent-line",
  // Art & Creativity
  "ri-brush-line", "ri-palette-line", "ri-paint-brush-line", "ri-scissors-line",
  "ri-image-line", "ri-camera-line", "ri-pencil-line", "ri-quill-pen-line",
  // Home & Space
  "ri-home-4-line", "ri-building-line", "ri-door-open-line", "ri-key-line",
  "ri-map-pin-line", "ri-compass-line", "ri-layout-line", "ri-stack-line",
  // Security & Trust
  "ri-shield-check-line", "ri-lock-line", "ri-eye-line", "ri-verified-badge-line",
  "ri-award-line", "ri-medal-line", "ri-star-line", "ri-trophy-line",
  // Communication
  "ri-mail-line", "ri-phone-line", "ri-message-line", "ri-chat-1-line",
  "ri-megaphone-line", "ri-notification-line", "ri-send-plane-line", "ri-discuss-line",
  // Time & Calendar
  "ri-time-line", "ri-calendar-line", "ri-calendar-check-line", "ri-alarm-line",
  "ri-history-line", "ri-refresh-line", "ri-loop-left-line", "ri-timer-line",
  // Nature & Misc
  "ri-leaf-line", "ri-sun-line", "ri-moon-line", "ri-sparkling-line",
  "ri-fire-line", "ri-water-flash-line", "ri-plant-line", "ri-flower-line",
  // Arrows & Actions
  "ri-arrow-right-line", "ri-arrow-up-line", "ri-check-line", "ri-add-line",
  "ri-close-line", "ri-delete-bin-line", "ri-edit-line", "ri-settings-line",
  // Social
  "ri-instagram-line", "ri-tiktok-line", "ri-youtube-line", "ri-twitter-x-line",
  "ri-linkedin-line", "ri-github-line", "ri-global-line", "ri-links-line",
  // Misc useful
  "ri-box-3-line", "ri-gift-line", "ri-truck-line", "ri-recycle-line",
  "ri-lightbulb-line", "ri-question-line", "ri-information-line", "ri-error-warning-line",
];

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  className?: string;
}

export default function IconPicker({ value, onChange, className = "" }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = search
    ? ICON_OPTIONS.filter((ic) => ic.includes(search.toLowerCase()))
    : ICON_OPTIONS;

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Change icon"
        className="w-8 h-8 flex items-center justify-center bg-pink-100 hover:bg-pink-200 rounded-full transition-colors cursor-pointer border-2 border-pink-300"
      >
        <i className={`${value} text-pink-500 text-base`}></i>
      </button>

      {open && (
        <div className="absolute z-50 top-10 left-0 bg-white border border-pink-100 rounded-2xl p-3 w-72 max-h-72 overflow-y-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search icons..."
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs mb-2 focus:outline-none focus:ring-2 focus:ring-pink-200"
            autoFocus
          />
          <div className="grid grid-cols-8 gap-1">
            {filtered.map((ic) => (
              <button
                key={ic}
                type="button"
                title={ic.replace("ri-", "").replace(/-line$/, "")}
                onClick={() => { onChange(ic); setOpen(false); setSearch(""); }}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  ic === value
                    ? "bg-pink-500 text-white"
                    : "hover:bg-pink-50 text-gray-600"
                }`}
              >
                <i className={`${ic} text-base`}></i>
              </button>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-2">No icons found</p>
          )}
        </div>
      )}
    </div>
  );
}
